/**
 * HeadToHeadPage.tsx - Phase 4 Navigation Integration
 * 
 * Wrapper page that extracts route params and renders HeadToHeadScoreboard.
 * This enables:
 * - Deep linking via /standings/:poolId/vs/:opponentUserId
 * - Back navigation to standings
 * - Route param extraction for the hook
 * 
 * FILE: ui/src/pages/HeadToHeadPage.tsx
 */

import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import HeadToHeadScoreboard from '../components/standings/HeadToHeadScoreboard';

/**
 * HeadToHeadPage - Route wrapper for Head-to-Head Scoreboard
 * 
 * Route: /standings/:poolId/vs/:opponentUserId
 * 
 * Responsibilities:
 * 1. Extract poolId and opponentUserId from URL params
 * 2. Get current user from auth context
 * 3. Provide onBack handler that navigates to standings
 * 4. Render HeadToHeadScoreboard with proper props
 */
export const HeadToHeadPage: React.FC = () => {
  const { poolId, opponentUserId } = useParams<{ 
    poolId: string; 
    opponentUserId: string; 
  }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  // Get current user ID from auth context
  const currentUserId = user?.user_id || 'larry';
  
  /**
   * Back navigation handler
   * Returns to GM hub leaderboard view (consistent with existing routing)
   */
  const handleBack = () => {
    // Navigate back to GM hub with leaderboard view
    // This is consistent with the existing GMLeaderboardRedirect pattern
    navigate(`/pools/${poolId}/leaderboard`)
  };
  
  // Guard: Missing required params
  if (!poolId) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-400 text-lg font-bold mb-2">Missing Pool ID</div>
          <button
            onClick={() => navigate('/')}
            className="px-4 py-2 bg-slate-800 hover:bg-slate-700 text-white rounded-lg text-sm"
          >
            Return to War Room
          </button>
        </div>
      </div>
    );
  }
  
  if (!opponentUserId) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-400 text-lg font-bold mb-2">Missing Opponent</div>
          <p className="text-slate-500 text-sm mb-4">No opponent selected for comparison</p>
          <button
            onClick={() => navigate(`/pools/${poolId}/leaderboard`)}
            className="px-4 py-2 bg-slate-800 hover:bg-slate-700 text-white rounded-lg text-sm"
          >
            Return to Standings
          </button>
        </div>
      </div>
    );
  }
  
  return (
    <HeadToHeadScoreboard
      poolId={poolId}
      opponentUserId={opponentUserId}
      currentUserId={currentUserId}
      onBack={handleBack}
    />
  );
};

export default HeadToHeadPage;
